import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Eye, Loader2, RotateCcw, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useOrg } from '@/contexts/OrgContext';
import { useToast } from '@/hooks/use-toast';
import { DangerousConfirm } from '@/components/platform/DangerousConfirm';
import { ViewSessionDialog } from '@/components/platform/ViewSessionDialog';

type TenantDetailData = {
  organization: { id: string; name: string; slug: string; created_at: string };
  subscription: {
    plan: string;
    status: string;
    trial_ends_at: string;
    stripe_status?: string | null;
    current_period_end?: string | null;
    cancel_at_period_end?: boolean | null;
    stripe_customer_id?: string | null;
    stripe_subscription_id?: string | null;
  } | null;
  members: Array<{ id: string; user_id: string; role: string; email: string | null; created_at: string }>;
  counts: { team_members: number; services: number; jobs: number; enquiries: number; callbacks: number };
  // Activity volume only. get_tenant_detail deliberately no longer returns raw
  // audit rows: those carried metadata/before_data/after_data — support reasons,
  // subscription and settings snapshots, actor ids — and handed them out on a
  // routine tenant page view, outside the reason-gated export path. The full
  // records live behind "View audit log", which goes through the reader RPC.
  audit_summary?: Array<{
    action: string;
    category: string;
    severity: string;
    events: number;
    last_at: string;
  }>;
};

type RpcClient = {
  rpc: (
    fn: 'get_tenant_detail' | 'suspend_tenant' | 'unsuspend_tenant',
    args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
};

const rpcClient = supabase as unknown as RpcClient;

const getErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return 'Unexpected error';
};

const TenantDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { startImpersonation } = useOrg();
  const [reason, setReason] = useState('');
  const [working, setWorking] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-tenant-detail', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const { data, error } = await rpcClient.rpc('get_tenant_detail', { _org_id: orgId });
      if (error) throw error;
      return data as TenantDetailData;
    },
  });

  const org = data?.organization;
  const isSuspended = data?.subscription?.status === 'canceled';
  const auditSummary = data?.audit_summary ?? [];

  const handleImpersonate = async (reason: string, caseRef?: string) => {
    if (!orgId) return;
    if (window.innerWidth < 768) {
      toast({ title: 'Impersonation is available on desktop only.' });
      return;
    }
    await startImpersonation(orgId, reason, caseRef);
    navigate('/admin');
  };

  const handleSuspend = async () => {
    if (!orgId) return;
    setWorking(true);
    try {
      const { error } = await rpcClient.rpc('suspend_tenant', { _org_id: orgId, _reason: reason || null });
      if (error) throw error;
      toast({ title: 'Tenant suspended' });
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['platform-tenant-detail', orgId] });
    } catch (error: unknown) {
      toast({ title: 'Unable to suspend tenant', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setWorking(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!orgId) return;
    setWorking(true);
    try {
      const { error } = await rpcClient.rpc('unsuspend_tenant', { _org_id: orgId });
      if (error) throw error;
      toast({ title: 'Tenant unsuspended' });
      await queryClient.invalidateQueries({ queryKey: ['platform-tenant-detail', orgId] });
    } catch (error: unknown) {
      toast({ title: 'Unable to unsuspend tenant', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setWorking(false);
    }
  };

  if (isLoading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (error || !data || !org) return <div className="p-8 text-destructive">Unable to load tenant.</div>;

  return (
    <div className="p-4 md:p-8">
      <Button variant="ghost" className="mb-4 gap-2" onClick={() => navigate('/platform/tenants')}>
        <ArrowLeft className="h-4 w-4" /> Back to tenants
      </Button>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="font-display text-3xl text-foreground">{org.name}</h2>
          <p className="mt-1 text-muted-foreground">{org.slug} · Created {new Date(org.created_at).toLocaleDateString()}</p>
          <div className="mt-3 flex gap-2">
            <Badge variant="outline">{data.subscription?.plan || 'no plan'}</Badge>
            <Badge variant={isSuspended ? 'destructive' : 'secondary'}>{data.subscription?.status || 'unknown'}</Badge>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <ViewSessionDialog
            tenantName={org.name}
            onConfirm={handleImpersonate}
            trigger={<Button className="gap-2"><Eye className="h-4 w-4" /> Impersonate this tenant</Button>}
          />
          <Button variant="outline" asChild><Link to={`/platform/audit?org=${org.id}`}>View audit log</Link></Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        {Object.entries(data.counts).map(([key, value]) => (
          <Card key={key}><CardHeader className="pb-2"><CardTitle className="text-sm capitalize text-muted-foreground">{key.replace('_', ' ')}</CardTitle></CardHeader><CardContent><div className="text-2xl font-semibold">{value}</div></CardContent></Card>
        ))}
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_360px]">
        <Card>
          <CardHeader><CardTitle>Members</CardTitle><CardDescription>Organisation account membership.</CardDescription></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.members.map((member) => (
                  <TableRow key={member.id}><TableCell>{member.email || member.user_id}</TableCell><TableCell><Badge variant="outline">{member.role}</Badge></TableCell><TableCell>{new Date(member.created_at).toLocaleDateString()}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="space-y-6">
        <Card>
          <CardHeader><CardTitle>Subscription</CardTitle><CardDescription>Stripe billing state for this tenant.</CardDescription></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between gap-4"><span className="text-muted-foreground">Plan</span><span className="font-medium capitalize">{data.subscription?.plan || '—'}</span></div>
            <div className="flex justify-between gap-4"><span className="text-muted-foreground">Status</span><Badge variant={isSuspended ? 'destructive' : 'secondary'}>{data.subscription?.status || 'unknown'}</Badge></div>
            {data.subscription?.stripe_status && (
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Stripe status</span><span className="font-mono text-xs">{data.subscription.stripe_status}</span></div>
            )}
            {data.subscription?.status === 'trialing' && data.subscription?.trial_ends_at && (
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Trial ends</span><span>{new Date(data.subscription.trial_ends_at).toLocaleDateString()}</span></div>
            )}
            {data.subscription?.current_period_end && (
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Renews</span><span>{new Date(data.subscription.current_period_end).toLocaleDateString()}</span></div>
            )}
            {data.subscription?.cancel_at_period_end && (
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Cancellation</span><Badge variant="destructive">Cancels at period end</Badge></div>
            )}
            <div className="flex justify-between gap-4"><span className="text-muted-foreground">Stripe customer</span><span className="font-mono text-xs">{data.subscription?.stripe_customer_id || 'none'}</span></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Tenant controls</CardTitle><CardDescription>Suspension uses canceled status for v1.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            {!isSuspended && (
              <div className="space-y-2">
                <Textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Suspension reason" />
                <DangerousConfirm
                  trigger={<Button variant="destructive" className="w-full gap-2"><ShieldOff className="h-4 w-4" /> Suspend tenant</Button>}
                  title="Suspend tenant?"
                  description="The tenant public site should show a holding state instead of normal booking flows."
                  confirmText="Suspend tenant"
                  expectedValue={org.slug}
                  onConfirm={handleSuspend}
                  loading={working}
                />
              </div>
            )}
            {isSuspended && (
              <DangerousConfirm
                trigger={<Button className="w-full gap-2"><RotateCcw className="h-4 w-4" /> Unsuspend tenant</Button>}
                title="Unsuspend tenant?"
                description="This restores the tenant to trialing status."
                confirmText="Unsuspend tenant"
                expectedValue={org.slug}
                onConfirm={handleUnsuspend}
                loading={working}
              />
            )}
          </CardContent>
        </Card>
        </div>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Audit activity (last 30 days)</CardTitle>
          <CardDescription>
            Event volume only. Open the audit log to see actors, reasons and record changes.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Action</TableHead><TableHead>Category</TableHead><TableHead>Severity</TableHead><TableHead>Events</TableHead><TableHead>Most recent</TableHead></TableRow></TableHeader>
            <TableBody>
              {auditSummary.map((entry) => (
                <TableRow key={`${entry.action}-${entry.severity}`}><TableCell>{entry.action}</TableCell><TableCell>{entry.category}</TableCell><TableCell>{entry.severity.replace(/_/g, ' ')}</TableCell><TableCell>{entry.events}</TableCell><TableCell>{new Date(entry.last_at).toLocaleString()}</TableCell></TableRow>
              ))}
              {auditSummary.length === 0 && <TableRow><TableCell colSpan={5} className="h-20 text-center text-muted-foreground">No audit events yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantDetail;
